import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
  alpha
} from '@mui/material'

import {
  getBranchPipelineConfig,
  listRepoBranches,
  searchRepoDt,
  triggerRepoPipeline
} from '../../api/socrInventory'
import { formatLastScannedLabel } from '../../utils/socrVulnBoard'

const formatMatchLabel = (match) => {
  if (!match) return ''
  const bits = [
    match.name || match.projectUuid,
    match.version ? `@ ${match.version}` : null,
    match.isLatest ? 'latest' : null,
    match.active === false ? 'inactive' : null
  ].filter(Boolean)
  return bits.join(' · ')
}

const DtMatchChips = ({ matches = [], created = null, projectName = '' }) => (
  <Stack direction='row' flexWrap='wrap' gap={0.5} sx={{ mt: 0.75 }}>
    {matches.length
      ? matches.map((m) => (
        <Chip
          key={m.projectUuid || `${m.name}-${m.version}`}
          size='small'
          color={m.isLatest ? 'success' : 'default'}
          label={[
            formatMatchLabel(m),
            m.lastBomImport ? formatLastScannedLabel(m.lastBomImport) : null
          ].filter(Boolean).join(' · ')}
          sx={{ maxWidth: '100%' }}
        />
      ))
      : <Chip size='small' label='No DT match yet' color='warning' />}
    {created
      ? (
        <Chip
          size='small'
          color='info'
          label={`Created ${created.name || projectName}${created.version ? ` @ ${created.version}` : ''}`}
        />
        )
      : null}
  </Stack>
)

const pickPreferredBranch = (list, defaultBranch = '') => {
  const names = (Array.isArray(list) ? list : []).map((b) => b?.name).filter(Boolean)
  const byName = (want) => names.find((n) => n.toLowerCase() === want)
  return (
    (defaultBranch && names.find((n) => n === defaultBranch)) ||
    list.find((b) => b.isDefault)?.name ||
    byName('main') ||
    byName('master') ||
    names[0] ||
    ''
  )
}

/**
 * Modal workflow: open → fetch branches once → pick branch → pipeline check →
 * DT search / createMissing → trigger SBOM pipeline.
 * Branches are only requested while the dialog is open (avoids Bitbucket spam).
 */
const RepoDtBuildDialog = ({
  open,
  onClose,
  token,
  repositoryUuid,
  repositoryLabel = '',
  defaultBranch = '',
  onInventoryChanged,
  onFlash
}) => {
  const [branches, setBranches] = useState([])
  const [branch, setBranch] = useState('')
  const [loadingBranches, setLoadingBranches] = useState(false)
  const [branchError, setBranchError] = useState(null)

  const [pipeCfg, setPipeCfg] = useState(null)
  const [loadingPipe, setLoadingPipe] = useState(false)

  const [searching, setSearching] = useState(false)
  const [creating, setCreating] = useState(false)
  const [triggering, setTriggering] = useState(false)
  const [searchResult, setSearchResult] = useState(null)
  const [pipelineResult, setPipelineResult] = useState(null)
  const [actionError, setActionError] = useState(null)

  // Reset + load branches only when the dialog opens for a repo
  useEffect(() => {
    if (!open || !token || !repositoryUuid) return undefined

    let cancelled = false
    setBranches([])
    setBranch('')
    setPipeCfg(null)
    setSearchResult(null)
    setPipelineResult(null)
    setActionError(null)
    setBranchError(null)
    setLoadingBranches(true)

    ;(async () => {
      try {
        const rows = await listRepoBranches(token, repositoryUuid)
        if (cancelled) return
        const list = Array.isArray(rows) ? rows : []
        setBranches(list)
        setBranch(pickPreferredBranch(list, defaultBranch))
      } catch (err) {
        if (!cancelled) {
          setBranches([])
          setBranch('')
          setBranchError(err.message || 'Failed to list branches')
        }
      } finally {
        if (!cancelled) setLoadingBranches(false)
      }
    })()

    return () => { cancelled = true }
  }, [open, token, repositoryUuid, defaultBranch])

  // Pipeline config only after a branch is chosen inside an open dialog
  useEffect(() => {
    if (!open || !token || !repositoryUuid || !branch) {
      setPipeCfg(null)
      return undefined
    }
    let cancelled = false
    setLoadingPipe(true)
    setPipeCfg(null)
    ;(async () => {
      try {
        const cfg = await getBranchPipelineConfig(token, repositoryUuid, branch)
        if (!cancelled) setPipeCfg(cfg)
      } catch {
        if (!cancelled) setPipeCfg(null)
      } finally {
        if (!cancelled) setLoadingPipe(false)
      }
    })()
    return () => { cancelled = true }
  }, [open, token, repositoryUuid, branch])

  const runSearch = useCallback(async ({ createMissing = false } = {}) => {
    if (!token || !repositoryUuid || !branch) return
    setActionError(null)
    if (createMissing) setCreating(true)
    else setSearching(true)
    try {
      const result = await searchRepoDt(token, repositoryUuid, {
        branch,
        createMissing,
        updateInventory: true
      })
      setSearchResult(result)
      onInventoryChanged?.()
      onFlash?.(
        'success',
        createMissing ? 'DT project shells created / inventory updated' : 'DT search complete'
      )
    } catch (err) {
      setActionError(err.message || 'DT search failed')
      onFlash?.('error', err.message || 'DT search failed')
    } finally {
      setSearching(false)
      setCreating(false)
    }
  }, [token, repositoryUuid, branch, onInventoryChanged, onFlash])

  const runTrigger = useCallback(async () => {
    if (!token || !repositoryUuid || !branch) return
    setActionError(null)
    setTriggering(true)
    try {
      const result = await triggerRepoPipeline(token, repositoryUuid, { branch })
      setPipelineResult(result)
      onFlash?.(
        'success',
        `Pipeline #${result.buildNumber ?? '?'} ${result.state || 'started'}`
      )
    } catch (err) {
      setActionError(err.message || 'Pipeline trigger failed')
      onFlash?.('error', err.message || 'Pipeline trigger failed')
    } finally {
      setTriggering(false)
    }
  }, [token, repositoryUuid, branch, onFlash])

  const canTrigger = Boolean(pipeCfg?.canTriggerDtBuild)
  const projects = Array.isArray(searchResult?.projects) ? searchResult.projects : []
  const missingAny = projects.some((p) => !Array.isArray(p.matches) || p.matches.length === 0)

  const pipelineHint = loadingPipe
    ? 'Checking pipeline config on this branch…'
    : pipeCfg
      ? (
          canTrigger
            ? 'Ready — bitbucket-pipelines.yml imports shared-pipelines on this branch.'
            : `Not ready: ${[
              !pipeCfg.hasPipelinesFile ? 'no bitbucket-pipelines.yml' : null,
              pipeCfg.hasPipelinesFile && !pipeCfg.hasSharedPipelinesImport
                ? 'missing shared-pipelines import'
                : null
            ].filter(Boolean).join(' · ') || 'pipeline not configured'}`
        )
      : branch
        ? 'Could not read pipeline config for this branch'
        : 'Select a branch'

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle sx={{ pb: 1 }}>
        Run Dependency Track scan
        {repositoryLabel
          ? (
            <Typography variant='body2' sx={{ color: alpha('#fff', 0.65), fontWeight: 600, mt: 0.5 }}>
              {repositoryLabel}
            </Typography>
            )
          : null}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Typography variant='body2' sx={{ color: alpha('#fff', 0.7) }}>
            Choose the branch to scan (usually <strong>main</strong> / <strong>master</strong>).
            We only load branches when this dialog opens, then check whether that branch has a
            Bitbucket pipeline step that can produce an SBOM.
          </Typography>

          {branchError ? <Alert severity='error'>{branchError}</Alert> : null}
          {actionError ? <Alert severity='error'>{actionError}</Alert> : null}

          {loadingBranches
            ? (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <CircularProgress size={28} />
                <Typography variant='caption' display='block' sx={{ mt: 1, color: alpha('#fff', 0.6) }}>
                  Loading branches from Bitbucket…
                </Typography>
              </Box>
              )
            : (
              <>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
                  <TextField
                    select
                    size='small'
                    label='Branch'
                    value={branch}
                    onChange={(e) => {
                      setBranch(e.target.value)
                      setSearchResult(null)
                      setPipelineResult(null)
                    }}
                    sx={{ minWidth: { sm: 220 }, flex: 1 }}
                    disabled={!branches.length}
                    helperText={
                      branches.length
                        ? `${branches.length} branch${branches.length === 1 ? '' : 'es'} loaded`
                        : 'No branches returned'
                    }
                  >
                    {branches.map((b) => (
                      <MenuItem key={b.name} value={b.name}>
                        {b.name}
                        {b.isDefault ? ' (default)' : ''}
                        {['main', 'master'].includes(String(b.name || '').toLowerCase()) ? ' ★' : ''}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button
                    size='small'
                    variant='outlined'
                    disabled={!branch || searching || creating}
                    onClick={() => runSearch({ createMissing: false })}
                    sx={{ mt: { sm: 0.5 } }}
                  >
                    {searching ? 'Searching…' : 'Find DT projects'}
                  </Button>
                </Stack>

                <Alert
                  severity={canTrigger ? 'success' : loadingPipe ? 'info' : 'warning'}
                  sx={{ py: 0.5 }}
                >
                  {pipelineHint}
                </Alert>

                {pipelineResult
                  ? (
                    <Typography variant='caption' sx={{ color: alpha('#fff', 0.85) }}>
                      Last run: #{pipelineResult.buildNumber} · {pipelineResult.state}
                      {pipelineResult.url
                        ? (
                          <>
                            {' · '}
                            <Link href={pipelineResult.url} target='_blank' rel='noopener noreferrer'>
                              open in Bitbucket
                            </Link>
                          </>
                          )
                        : null}
                    </Typography>
                    )
                  : null}

                {projects.length
                  ? (
                    <Stack spacing={1}>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.82rem' }}>
                        Projects on {branch}
                      </Typography>
                      {projects.map((proj) => {
                        const matches = Array.isArray(proj.matches) ? proj.matches : []
                        return (
                          <Box
                            key={`${proj.projectName}-${proj.csprojPath}`}
                            sx={{
                              p: 1,
                              borderRadius: 1,
                              border: `1px solid ${alpha('#fff', 0.1)}`,
                              backgroundColor: alpha('#000', 0.2)
                            }}
                          >
                            <Typography sx={{ fontWeight: 700, fontSize: '0.82rem' }}>
                              {proj.projectName}
                              {proj.runtime ? ` · ${proj.runtime}` : ''}
                            </Typography>
                            {proj.csprojPath
                              ? (
                                <Typography variant='caption' sx={{ color: alpha('#fff', 0.55), display: 'block' }}>
                                  {proj.csprojPath}
                                </Typography>
                                )
                              : null}
                            <DtMatchChips
                              matches={matches}
                              created={proj.created}
                              projectName={proj.projectName}
                            />
                          </Box>
                        )
                      })}
                      {missingAny
                        ? (
                          <Button
                            size='small'
                            variant='outlined'
                            color='warning'
                            disabled={creating || searching}
                            onClick={() => runSearch({ createMissing: true })}
                            sx={{ alignSelf: 'flex-start' }}
                          >
                            {creating ? 'Creating…' : 'Create missing DT projects'}
                          </Button>
                          )
                        : null}
                    </Stack>
                    )
                  : searchResult
                    ? (
                      <Typography variant='caption' sx={{ color: alpha('#fff', 0.55) }}>
                        No .csproj projects found on this branch
                      </Typography>
                      )
                    : null}
              </>
              )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant='contained'
          disabled={!branch || !canTrigger || triggering || loadingPipe || loadingBranches}
          onClick={runTrigger}
          title={canTrigger ? 'Start Bitbucket pipeline for SBOM import' : 'Selected branch needs shared-pipelines'}
        >
          {triggering ? 'Starting…' : 'Start SBOM pipeline'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export { RepoDtBuildDialog }
/** @deprecated use RepoDtBuildDialog */
export { RepoDtBuildDialog as RepoDtPanel }
